import { LitElement, html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { ChevronRight, ChevronLeft, ListTree, AlertTriangle, Search } from 'lucide';
import { lucideIcon } from '../icon.js';
import { getCodelistDomains } from '../data/codelist-registry.js';

const STD_LABEL = { nen5104: 'NEN 5104', nen6693: 'NEN 6693' };

/**
 * Admin > Codelijsten — read-only referentie van elke dropdown/domein in de app,
 * à la codes.sikb.nl. Toont per waarde: in welke NEN-standaard we hem tonen en naar
 * welke TI/SIKB-code hij bij export vertaalt (rood = geen schone TI-code = exportverlies).
 * Volledig afgeleid uit @mv/models (single source of truth).
 */
export class AdminCodelists extends LitElement {
  createRenderRoot() { return this; }

  static properties = {
    /** Domein-id uit de URL (/admin/settings/codelists/:id); leeg = overzicht. */
    domainId: { type: String },
    selectedId: { type: String },
    query: { type: String },
  };

  constructor() {
    super();
    this.selectedId = null;
    this.domainId = null;
    this.query = '';
    this.domains = getCodelistDomains();
  }

  /** Navigeren i.p.v. alleen state zetten: elk domein heeft een eigen URL, dus de
   *  terugknop van de browser werkt en je kunt een domein delen/bookmarken. */
  _select(id) { this._navigate(`/admin/settings/codelists/${id}`); }
  _back() { this._navigate('/admin/settings/codelists'); }

  _navigate(path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  updated(changed) {
    if (!changed.has('domainId')) return;
    this.selectedId = this.domainId || null;
    this.query = '';
    if (this.domainId) window.scrollTo?.(0, 0);
  }

  _stdBadges(standards) {
    return html`${standards.map(s => html`<span style="display:inline-block; font-size:0.7rem; font-weight:700; padding:1px 7px; border-radius:999px; margin-right:4px;
      background:${s === 'nen6693' ? '#e7f0ff' : '#eef7ee'}; color:${s === 'nen6693' ? '#1c4fb3' : '#2e7d32'};">${STD_LABEL[s]}</span>`)}`;
  }

  _tiCell(v) {
    if (v.tiStatus === 'mapped') return html`<code style="font-weight:700;">${v.tiCode}</code>`;
    if (v.tiStatus === 'na') return html`<span style="color:var(--mv-text-sub,#6c757d); font-size:0.82rem;">n.v.t.</span>`;
    // missing → rood (exportverlies)
    return html`<span style="display:inline-flex; align-items:center; gap:4px; color:var(--mv-danger,#c0392b); font-weight:700; font-size:0.82rem;">
      ${unsafeSVG(lucideIcon(AlertTriangle, 13))} geen TI-code</span>`;
  }

  // ---- Overzicht (domeinen) ----
  _renderList() {
    const th = 'text-align:left; padding:10px 12px; font-size:0.74rem; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-sub,#6c757d); border-bottom:1px solid var(--mv-border);';
    return html`
      <div style="border:1px solid var(--mv-border); border-radius:12px; overflow:hidden; background:var(--mv-surface,#fff);">
        <table style="width:100%; border-collapse:collapse;">
          <thead><tr>
            <th style="${th}">Domein</th>
            <th style="${th}">Omschrijving</th>
            <th style="${th}">Waarden</th>
            <th style="${th}">Standaarden</th>
            <th style="${th}">TerraIndex</th>
            <th style="${th}"></th>
          </tr></thead>
          <tbody>
            ${this.domains.map(d => html`
              <tr @click=${() => this._select(d.id)} style="cursor:pointer; border-bottom:1px solid var(--mv-border);"
                  @mouseover=${e => e.currentTarget.style.background = 'var(--mv-bg,#f7f8fa)'}
                  @mouseout=${e => e.currentTarget.style.background = ''}>
                <td style="padding:12px; font-weight:700; color:var(--mv-primary,#1c4fb3);">${d.name}</td>
                <td style="padding:12px; color:var(--mv-text-sub,#6c757d); font-size:0.86rem;">${d.description}</td>
                <td style="padding:12px; font-variant-numeric:tabular-nums;">${d.count}</td>
                <td style="padding:12px;">${this._stdBadges(d.standards)}</td>
                <td style="padding:12px; font-variant-numeric:tabular-nums;">${this._tiCell(d)}</td>
                <td style="padding:12px; text-align:right; white-space:nowrap;">
                  <span style="color:var(--mv-text-sub,#6c757d);">${unsafeSVG(lucideIcon(ChevronRight, 18))}</span>
                </td>
              </tr>`)}
          </tbody>
        </table>
      </div>`;
  }

  /**
   * Kolom TerraIndex: hoeveel waarden in dit domein GEEN TI-koppeling hebben.
   * Twee smaken, want die betekenen iets anders — de tooltip splitst ze uit:
   *   • ontbrekend (rood)  — TerraIndex heeft het veld wél, wij vullen het niet.
   *   • n.v.t. (grijs)     — TerraIndex kent dit concept niet; er valt niets te mappen.
   */
  _tiCell(d) {
    const unmapped = d.missingCount + d.naCount;
    if (!unmapped) {
      return html`<span title="Alle waarden zijn gekoppeld aan een TerraIndex-code"
        style="color:var(--mv-text-sub,#6c757d);">—</span>`;
    }
    const parts = [];
    if (d.missingCount) parts.push(`${d.missingCount} zonder TI-code`);
    if (d.naCount) parts.push(`${d.naCount} niet van toepassing (TerraIndex kent dit veld niet)`);
    const alarming = d.missingCount > 0;
    return html`<span title="${parts.join(' · ')}"
      style="display:inline-flex; align-items:center; gap:4px; font-size:0.8rem; font-weight:700;
        color:${alarming ? 'var(--mv-danger,#c0392b)' : 'var(--mv-text-sub,#6c757d)'};">
      ${alarming ? unsafeSVG(lucideIcon(AlertTriangle, 12)) : ''}${unmapped}
    </span>`;
  }

  // ---- Detail (waarden van één domein) ----
  _renderDetail(d) {
    const q = this.query.trim().toLowerCase();
    const match = v => !q || v.label.toLowerCase().includes(q) || String(v.code || '').toLowerCase().includes(q) || String(v.tiCode || '').toLowerCase().includes(q);
    const th = 'text-align:left; padding:9px 12px; font-size:0.72rem; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-sub,#6c757d); border-bottom:1px solid var(--mv-border); position:sticky; top:0; background:var(--mv-surface,#fff);';
    const td = 'padding:9px 12px; border-bottom:1px solid var(--mv-border); font-size:0.88rem; vertical-align:top;';
    return html`
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:6px;">
        <button @click=${this._back} style="display:inline-flex; align-items:center; gap:5px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); border-radius:8px; padding:6px 12px; cursor:pointer; color:var(--mv-text); font-size:0.85rem;">
          ${unsafeSVG(lucideIcon(ChevronLeft, 15))} Alle domeinen
        </button>
      </div>
      <h2 style="margin:0 0 2px; font-size:1.25rem;">${d.name}</h2>
      <p style="margin:0 0 14px; color:var(--mv-text-sub,#6c757d); font-size:0.9rem;">${d.description}</p>

      <div style="position:relative; max-width:320px; margin-bottom:12px;">
        <span style="position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--mv-text-sub,#6c757d);">${unsafeSVG(lucideIcon(Search, 15))}</span>
        <input .value=${this.query} @input=${e => this.query = e.target.value} placeholder="Zoek waarde of code…"
          style="width:100%; padding:7px 10px 7px 32px; border:1px solid var(--mv-border); border-radius:8px; font-size:0.86rem; box-sizing:border-box; background:var(--mv-surface,#fff); color:var(--mv-text);">
      </div>

      <div style="border:1px solid var(--mv-border); border-radius:12px; overflow:hidden; background:var(--mv-surface,#fff);">
        <table style="width:100%; border-collapse:collapse;">
          <thead><tr>
            <th style="${th}">Waarde</th>
            <th style="${th}">Interne code</th>
            <th style="${th}">NEN 5104</th>
            <th style="${th}">NEN 6693</th>
            <th style="${th}">TI-/SIKB-code</th>
            <th style="${th}">Definitie / opmerking</th>
          </tr></thead>
          <tbody>
            ${d.groups.map(g => {
              const rows = g.values.filter(match);
              if (!rows.length) return '';
              return html`
                ${g.label ? html`<tr><td colspan="6" style="padding:8px 12px; background:var(--mv-bg,#f7f8fa); font-weight:700; font-size:0.78rem; text-transform:uppercase; letter-spacing:0.03em; color:var(--mv-text-sub,#6c757d);">${g.label}</td></tr>` : ''}
                ${rows.map(v => html`
                  <tr>
                    <td style="${td} font-weight:600;">${v.label}</td>
                    <td style="${td}">${v.code ? html`<code>${v.code}</code>` : html`<span style="color:var(--mv-text-sub,#6c757d);">—</span>`}</td>
                    <td style="${td}">${v.standards.includes('nen5104') ? html`<span style="color:#2e7d32; font-weight:700;">✓</span>` : html`<span style="color:var(--mv-border);">–</span>`}</td>
                    <td style="${td}">${v.standards.includes('nen6693') ? html`<span style="color:#1c4fb3; font-weight:700;">✓</span>` : html`<span style="color:var(--mv-border);">–</span>`}</td>
                    <td style="${td}">${this._tiCell(v)}</td>
                    <td style="${td} color:var(--mv-text-sub,#6c757d); font-size:0.82rem; max-width:360px;">${v.definition || ''}</td>
                  </tr>`)}`;
            })}
          </tbody>
        </table>
      </div>`;
  }

  render() {
    const d = this.selectedId && this.domains.find(x => x.id === this.selectedId);
    return html`
      <div style="max-width:1100px; margin:0 auto; padding:24px 20px;">
        <div style="display:flex; align-items:center; gap:10px; margin-bottom:4px;">
          <span style="color:var(--mv-primary,#1c4fb3);">${unsafeSVG(lucideIcon(ListTree, 22))}</span>
          <h1 style="margin:0; font-size:1.5rem;">Codelijsten</h1>
        </div>
        <p style="margin:0 0 20px; color:var(--mv-text-sub,#6c757d); font-size:0.92rem;">
          Elke dropdown/domein in de app, met de mogelijke waarden, in welke NEN-standaard we ze tonen en
          waar ze naartoe vertalen bij de TerraIndex-export. Read-only — afgeleid uit <code>@mv/models</code>.
        </p>
        ${d ? this._renderDetail(d) : this._renderList()}
      </div>`;
  }
}

customElements.define('admin-codelists', AdminCodelists);
